/**
 * Réservation d'un cours particulier avec son prof attitré (lien tutor_links
 * actif — l'élève n'a qu'un seul tuteur pour l'instant, cf. CLAUDE.md). Les
 * créneaux affichés sont filtrés sur le niveau de l'élève (le serveur
 * réapplique la garde dans reserve_tutor_booking, cf. migration 0215).
 */

import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, CalendarClock } from 'lucide-react';
import { Button, Paginator } from '@jeraisonne/ui';
import { SCHOOL_LEVELS } from '@jeraisonne/db';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/lib/supabase';
import {
  useTutorBookableSlots,
  useBookSlot,
  useMyUpcomingBookings,
  useMyPastBookings,
  type BookableSlot,
} from '@/lib/booking/booking-slots';
import {
  UpcomingRendezVousCard,
  formatRendezVousDate,
} from '@/components/upcoming-rendezvous-card';
import { PastRendezVousCard } from '@/components/past-rendezvous-card';
import { usePagedList } from '@/lib/use-paged-list';

function formatSlotDate(iso: string): string {
  return formatRendezVousDate(iso);
}

function formatPrice(cents: number): string {
  return (cents / 100).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
}

function useMyActiveTutor(studentId: string | undefined) {
  return useQuery({
    queryKey: ['my-active-tutor', studentId],
    enabled: !!studentId,
    queryFn: async (): Promise<{ id: string; display_name: string } | null> => {
      const { data, error } = await supabase
        .from('tutor_links')
        .select('profiles!tutor_links_tutor_id_fkey ( id, display_name:visible_name )')
        .eq('student_id', studentId!)
        .eq('status', 'active')
        .limit(1)
        .maybeSingle();
      if (error) throw new Error(error.message);
      const profiles = (
        data as unknown as { profiles: { id: string; display_name: string } | null } | null
      )?.profiles;
      return profiles ?? null;
    },
  });
}

function useMyLevel(studentId: string | undefined) {
  return useQuery({
    queryKey: ['my-level', studentId],
    enabled: !!studentId,
    queryFn: async (): Promise<string | null> => {
      const { data, error } = await supabase
        .from('student_profiles')
        .select('level')
        .eq('id', studentId!)
        .maybeSingle();
      if (error) throw new Error(error.message);
      return data?.level ?? null;
    },
  });
}

function levelLabel(level: string): string {
  return SCHOOL_LEVELS.find((l) => l.value === level)?.label ?? level;
}

function SlotCard({
  slot,
  onBook,
  booking,
}: {
  slot: BookableSlot;
  onBook: () => void;
  booking: boolean;
}) {
  const full = slot.seats_left <= 0;
  return (
    <div className="border-ink-100 rounded-2xl border bg-white p-4">
      <p className="font-display text-ink-900 text-sm font-bold first-letter:uppercase">
        {formatSlotDate(slot.starts_at)}
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <span className="bg-primary-50 text-primary-700 rounded-full px-2.5 py-1 text-xs font-semibold">
          {levelLabel(slot.level)}
        </span>
        {slot.chapter_title && (
          <span className="bg-ink-100 text-ink-700 rounded-full px-2.5 py-1 text-xs font-semibold">
            {slot.chapter_title}
          </span>
        )}
        <span className="text-ink-500 font-sans text-xs">
          {full ? 'Complet' : `${slot.seats_left}/${slot.capacity} places`}
        </span>
      </div>
      <div className="mt-3 flex items-center justify-between gap-3">
        <p className="font-display text-ink-900 text-base font-extrabold">
          {formatPrice(slot.price_cents)}
        </p>
        <Button onClick={onBook} disabled={full || booking}>
          {full ? 'Complet' : booking ? 'Redirection…' : 'Réserver'}
        </Button>
      </div>
    </div>
  );
}

export function StudentReserverCoursPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: tutor, isLoading: tutorLoading } = useMyActiveTutor(user?.id);
  const { data: level } = useMyLevel(user?.id);
  const { data: upcomingBookings = [] } = useMyUpcomingBookings(user?.id);
  const { data: pastBookings = [] } = useMyPastBookings(user?.id);
  const { items: pagePast, paginator: pastPaginator } = usePagedList(pastBookings, {
    unit: 'rendez-vous',
    label: 'Pages des rendez-vous passés',
  });
  const { data: slots = [], isLoading: slotsLoading } = useTutorBookableSlots(tutor?.id);
  const book = useBookSlot();
  const [pendingSlotId, setPendingSlotId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const visibleSlots = level ? slots.filter((s) => s.level === level) : slots;

  const handleBook = async (slotId: string) => {
    setError(null);
    setPendingSlotId(slotId);
    try {
      const { url } = await book.mutateAsync({ slotId, returnTo: window.location.href });
      window.location.href = url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Réservation impossible.');
      setPendingSlotId(null);
    }
  };

  return (
    <div className="mx-auto max-w-lg px-4 py-6 sm:py-8">
      <button
        type="button"
        onClick={() => navigate({ to: '/student/contacts' })}
        aria-label="Retour"
        className="border-ink-200 text-ink-700 hover:bg-ink-50 mb-3 flex h-11 w-11 items-center justify-center rounded-full border bg-white shadow-sm transition-colors active:scale-95"
      >
        <ArrowLeft className="h-5 w-5" />
      </button>
      <h1 className="font-display text-ink-900 text-2xl font-extrabold tracking-tight">
        Rendez-vous
      </h1>
      <p className="text-ink-500 mt-1 font-sans text-sm">
        {tutor
          ? `Créneaux disponibles avec ${tutor.display_name}.`
          : 'Créneaux disponibles avec ton prof.'}
      </p>

      {upcomingBookings.length > 0 && (
        <section className="mt-5 space-y-2">
          <p className="text-ink-400 text-xs font-semibold uppercase tracking-wider">
            Mes prochains rendez-vous
          </p>
          {upcomingBookings.map((booking) => (
            <UpcomingRendezVousCard
              key={booking.reservationId}
              item={{
                reservationId: booking.reservationId,
                otherPartyName: booking.tutorName,
                startsAt: booking.startsAt,
                endsAt: booking.endsAt,
                kind: booking.kind,
              }}
            />
          ))}
        </section>
      )}

      <div className="mt-5 space-y-3">
        {(tutorLoading || slotsLoading) && (
          <div className="flex justify-center py-10">
            <div className="border-primary-400 h-8 w-8 animate-spin rounded-full border-2 border-t-transparent" />
          </div>
        )}

        {!tutorLoading && !tutor && (
          <div className="border-ink-100 rounded-2xl border bg-white p-6 text-center">
            <CalendarClock className="text-ink-300 mx-auto h-8 w-8" />
            <p className="text-ink-700 mt-2 font-sans text-sm font-semibold">
              Pas encore de prof attitré
            </p>
            <p className="text-ink-500 mt-1 font-sans text-xs">
              Une fois lié à un tuteur, ses créneaux de cours particuliers apparaîtront ici.
            </p>
          </div>
        )}

        {tutor && !slotsLoading && visibleSlots.length === 0 && (
          <div className="border-ink-100 rounded-2xl border bg-white p-6 text-center">
            <CalendarClock className="text-ink-300 mx-auto h-8 w-8" />
            <p className="text-ink-700 mt-2 font-sans text-sm font-semibold">
              Aucun créneau disponible
            </p>
            <p className="text-ink-500 mt-1 font-sans text-xs">Reviens un peu plus tard.</p>
          </div>
        )}

        {error && <p className="text-accent-600 font-sans text-xs">{error}</p>}

        {visibleSlots.map((slot) => (
          <SlotCard
            key={slot.id}
            slot={slot}
            onBook={() => handleBook(slot.id)}
            booking={book.isPending && pendingSlotId === slot.id}
          />
        ))}
      </div>

      {pastBookings.length > 0 && (
        <section className="mt-6 space-y-2">
          <p className="text-ink-400 text-xs font-semibold uppercase tracking-wider">
            Rendez-vous passés
          </p>
          {pagePast.map((booking) => (
            <PastRendezVousCard
              key={booking.reservationId}
              item={{
                reservationId: booking.reservationId,
                otherPartyName: booking.tutorName,
                startsAt: booking.startsAt,
                kind: booking.kind,
                status: booking.status,
                archivedByMe: booking.archivedByMe,
              }}
            />
          ))}
          <Paginator {...pastPaginator} />
        </section>
      )}
    </div>
  );
}
